// src/components/email/UploadCSV.jsx
// ═══════════════════════════════════════════════════════════════════════════
// 📎 UPLOAD DE CSV PARA EMAIL MARKETING
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useRef } from 'react';
import api from '../../services/api';
import Swal from 'sweetalert2';

const UploadCSV = ({ onUploadComplete, onCancel }) => {
  const [archivo, setArchivo] = useState(null);
  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [validando, setValidando] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isDragReject, setIsDragReject] = useState(false);
  const inputRef = useRef(null);

  const procesarArchivo = (file) => {
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      Swal.fire({
        icon: 'warning',
        title: 'Archivo Inválido',
        text: 'Solo se permiten archivos CSV'
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: 'warning',
        title: 'Archivo Muy Grande',
        text: 'El archivo no debe superar los 5MB'
      });
      return;
    }

    setArchivo(file);
    setVistaPrevia(null);
    validarArchivo(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (subiendo || validando) return;
    const file = e.dataTransfer.items?.[0];
    if (file && file.type !== 'text/csv' && !file.getAsFile?.()?.name?.endsWith('.csv')) {
      setIsDragReject(true);
    } else {
      setIsDragActive(true);
      setIsDragReject(false);
    }
  };

  const handleDragLeave = () => {
    setIsDragActive(false);
    setIsDragReject(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    setIsDragReject(false);
    if (subiendo || validando) return;
    const file = e.dataTransfer.files?.[0];
    if (file) procesarArchivo(file);
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) procesarArchivo(file);
    e.target.value = '';
  };

  const validarArchivo = async (file) => {
    try {
      setValidando(true);

      const formData = new FormData();
      formData.append('archivo', file);

      const res = await api.post('/email/csv/validar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setVistaPrevia(res.data);

      Swal.fire({
        icon: 'success',
        title: 'CSV Válido',
        html: `
          <p><strong>Columnas detectadas:</strong> ${res.data.columnas.join(', ')}</p>
          <p><strong>Total filas:</strong> ${res.data.filas_totales}</p>
        `,
        timer: 3000,
        showConfirmButton: false
      });

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'No se pudo validar el CSV'
      });
      setVistaPrevia(null);
    } finally {
      setValidando(false);
    }
  };

  const handleSubir = async () => {
    if (!archivo) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin Archivo',
        text: 'Selecciona un archivo CSV primero'
      });
      return;
    }

    try {
      setSubiendo(true);

      const formData = new FormData();
      formData.append('archivo', archivo);

      const res = await api.post('/email/csv/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      Swal.fire({
        icon: 'success',
        title: '✅ CSV Subido',
        html: `
          <p><strong>Emails válidos:</strong> ${res.data.emails_validos}</p>
          <p><strong>Emails inválidos:</strong> ${res.data.emails_invalidos}</p>
        `,
        timer: 2000,
        showConfirmButton: false
      });

      if (onUploadComplete) {
        onUploadComplete(res.data);
      }

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'No se pudo subir el CSV'
      });
    } finally {
      setSubiendo(false);
    }
  };

  const handleLimpiar = () => {
    setArchivo(null);
    setVistaPrevia(null);
  };

  // Estilos dinámicos del área de drop
  const dropAreaStyle = {
    ...styles.uploadArea,
    ...(isDragActive && !isDragReject ? styles.uploadAreaActive : {}),
    ...(isDragReject ? styles.uploadAreaReject : {}),
    ...(archivo ? styles.uploadAreaWithFile : {})
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>📎 Subir Archivo CSV</h2>
        <button onClick={onCancel} style={styles.btnClose}>✕</button>
      </div>

      {/* Área de Drag & Drop */}
      <div
        style={dropAreaStyle}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !subiendo && !validando && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={handleInputChange}
        />

        {isDragReject ? (
          <>
            <div style={styles.uploadIcon}>❌</div>
            <p style={{ ...styles.uploadText, color: '#ef4444' }}>
              Archivo no permitido — solo CSV de máximo 5MB
            </p>
          </>
        ) : isDragActive ? (
          <>
            <div style={styles.uploadIcon}>📂</div>
            <p style={{ ...styles.uploadText, color: '#3b82f6', fontWeight: '600' }}>
              Suelta el archivo aquí...
            </p>
          </>
        ) : archivo ? (
          <>
            <div style={styles.uploadIcon}>✅</div>
            <p style={{ ...styles.uploadText, color: '#059669', fontWeight: '600' }}>
              {archivo.name}
            </p>
            <p style={styles.uploadHint}>
              {(archivo.size / 1024).toFixed(1)} KB · Haz click o arrastra para cambiar
            </p>
          </>
        ) : (
          <>
            <div style={styles.uploadIcon}>📄</div>
            <p style={styles.uploadText}>
              Arrastra tu archivo CSV aquí o haz click para seleccionar
            </p>
            <p style={styles.uploadHint}>Máximo 5MB · Solo archivos CSV</p>
          </>
        )}
      </div>

      {/* Validando */}
      {validando && (
        <div style={styles.loadingBox}>
          <div style={styles.spinner}></div>
          <p>Validando archivo...</p>
        </div>
      )}

      {/* Vista Previa */}
      {vistaPrevia && !validando && (
        <div style={styles.previewCard}>
          <h3 style={styles.previewTitle}>📊 Vista Previa del CSV</h3>

          <div style={styles.previewStats}>
            <div style={styles.stat}>
              <span style={styles.statLabel}>Columnas</span>
              <span style={styles.statValue}>{vistaPrevia.columnas?.length || 0}</span>
            </div>
            <div style={styles.stat}>
              <span style={styles.statLabel}>Filas</span>
              <span style={styles.statValue}>{vistaPrevia.filas_totales || 0}</span>
            </div>
          </div>

          <div style={styles.columnasList}>
            <span style={styles.columnasLabel}>Columnas detectadas:</span>
            <div style={styles.columnasTags}>
              {vistaPrevia.columnas?.map((col, i) => (
                <span
                  key={i}
                  style={{
                    ...styles.columnTag,
                    ...(col === 'email' ? styles.columnTagEmail : {})
                  }}
                >
                  {col}
                </span>
              ))}
            </div>
          </div>

          {vistaPrevia.muestra && vistaPrevia.muestra.length > 0 && (
            <div style={styles.muestraTable}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {Object.keys(vistaPrevia.muestra[0]).map((key, i) => (
                      <th key={i} style={styles.th}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vistaPrevia.muestra.map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((val, j) => (
                        <td key={j} style={styles.td}>{val || '-'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!vistaPrevia.columnas?.includes('email') && (
            <div style={styles.warningBox}>
              ⚠️ <strong>Importante:</strong> El CSV debe tener una columna llamada "email"
            </div>
          )}
        </div>
      )}

      {/* Botones de Acción */}
      <div style={styles.actions}>
        <button
          onClick={handleLimpiar}
          style={styles.btnSecondary}
          disabled={subiendo || !archivo}
        >
          🗑️ Limpiar
        </button>
        <button
          onClick={handleSubir}
          style={{
            ...styles.btnPrimary,
            opacity: (!archivo || !vistaPrevia || subiendo) ? 0.6 : 1,
            cursor: (!archivo || !vistaPrevia || subiendo) ? 'not-allowed' : 'pointer'
          }}
          disabled={!archivo || !vistaPrevia || subiendo}
        >
          {subiendo ? '⏳ Subiendo...' : '✅ Subir CSV'}
        </button>
      </div>

      {/* Instrucciones */}
      <div style={styles.infoBox}>
        <h4 style={styles.infoTitle}>💡 Formato CSV Requerido</h4>
        <ul style={styles.infoList}>
          <li><strong>email</strong> (obligatorio) - Correo del destinatario</li>
          <li><strong>nombre</strong> (opcional) - Nombre para personalización</li>
          <li><strong>empresa</strong> (opcional) - Empresa para personalización</li>
          <li><strong>telefono</strong> (opcional) - Teléfono de contacto</li>
        </ul>
        <p style={styles.infoExample}>
          <strong>Ejemplo:</strong> nombre,email,empresa,telefono
        </p>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS
// ═══════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    maxWidth: '700px',
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid #e5e7eb'
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  btnClose: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#64748b',
    padding: '4px 8px'
  },
  uploadArea: {
    border: '2px dashed #cbd5e1',
    borderRadius: '12px',
    padding: '48px 20px',
    textAlign: 'center',
    background: '#f8fafc',
    marginBottom: '24px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    outline: 'none'
  },
  uploadAreaActive: {
    border: '2px dashed #3b82f6',
    background: '#eff6ff',
    transform: 'scale(1.01)',
    boxShadow: '0 0 0 4px rgba(59,130,246,0.15)'
  },
  uploadAreaReject: {
    border: '2px dashed #ef4444',
    background: '#fef2f2',
    boxShadow: '0 0 0 4px rgba(239,68,68,0.1)'
  },
  uploadAreaWithFile: {
    border: '2px dashed #10b981',
    background: '#f0fdf4'
  },
  uploadIcon: {
    fontSize: '48px',
    marginBottom: '12px'
  },
  uploadText: {
    fontSize: '14px',
    color: '#475569',
    marginBottom: '8px'
  },
  uploadHint: {
    fontSize: '12px',
    color: '#94a3b8'
  },
  previewCard: {
    background: '#f0f9ff',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '24px',
    border: '1px solid #bae6fd'
  },
  previewTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '16px'
  },
  previewStats: {
    display: 'flex',
    gap: '24px',
    marginBottom: '16px'
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  statLabel: {
    fontSize: '11px',
    color: '#64748b',
    textTransform: 'uppercase',
    fontWeight: '600'
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b'
  },
  columnasList: {
    marginBottom: '16px'
  },
  columnasLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '600',
    display: 'block',
    marginBottom: '8px'
  },
  columnasTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  columnTag: {
    padding: '4px 10px',
    background: '#dbeafe',
    color: '#1e40af',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600'
  },
  columnTagEmail: {
    background: '#d1fae5',
    color: '#065f46'
  },
  muestraTable: {
    overflow: 'hidden',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    marginTop: '12px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    padding: '12px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    color: '#64748b',
    background: '#f8fafc',
    borderBottom: '2px solid #e2e8f0'
  },
  td: {
    padding: '12px',
    fontSize: '13px',
    color: '#1e293b',
    borderBottom: '1px solid #e2e8f0'
  },
  warningBox: {
    padding: '12px',
    background: '#fef3c7',
    borderRadius: '8px',
    color: '#92400e',
    fontSize: '13px',
    marginTop: '16px'
  },
  loadingBox: {
    textAlign: 'center',
    padding: '24px',
    color: '#64748b',
    marginBottom: '16px'
  },
  spinner: {
    width: '36px',
    height: '36px',
    border: '4px solid #e2e8f0',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 12px auto'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px'
  },
  btnPrimary: {
    flex: 1,
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    fontSize: '14px',
    transition: 'all 0.2s'
  },
  btnSecondary: {
    flex: 1,
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  infoBox: {
    padding: '16px',
    background: '#eff6ff',
    borderRadius: '8px',
    borderLeft: '4px solid #3b82f6'
  },
  infoTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#1e40af',
    marginBottom: '12px'
  },
  infoList: {
    margin: '0 0 12px 0',
    paddingLeft: '20px',
    fontSize: '12px',
    color: '#1e40af',
    lineHeight: '2'
  },
  infoExample: {
    fontSize: '11px',
    color: '#64748b',
    margin: 0,
    fontFamily: 'monospace',
    background: 'white',
    padding: '8px',
    borderRadius: '4px'
  }
};

export default UploadCSV;
